import { CONSTANTS } from "../constants/constants";
import { UPLOAD_FILES_SERVICE } from "./ServiceConstants";
import { ENDPOINTS, API_BASE_URL } from "./endpoints";

export const fetchUserFiles = async (): Promise<any> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS?.USER_DOCUMENTS(
        sessionStorage.getItem(CONSTANTS?.USER_ID) ?? ""
      )}`,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
        },
      }
    );
    if (!response?.ok) throw new Error("Failed to fetch user files");
    const data = response?.json();
    return data;
  } catch (error) {
    console.error("Error fetching user files:", error);
    throw error;
  }
};

export const fetchFilePreview = async (fileId: string): Promise<Blob> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS?.FILE_DOWNLOAD(fileId)}`,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
        },
      }
    );
    if (!response?.ok) throw new Error("Failed to fetch file preview");
    return await response?.blob();
  } catch (error) {
    console.error("Error fetching file preview:", error);
    throw error;
  }
};

export const deleteFileById = async (fileId: string): Promise<void> => {
  const formData = new FormData();
  formData?.append("doc_id", fileId);

  try {
    const response = await fetch(`${API_BASE_URL}${ENDPOINTS?.FILE_DELETE}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem(
          CONSTANTS?.REACT_TOKEN
        )}`,
      },
      body: formData,
    });

    if (!response?.ok) throw new Error("Failed to delete file");
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
};

export const uploadFile = async (
  file: File,
  domain: string,
  subDomain: string,
  courseName: string
) => {
  if (file && domain && subDomain) {
    try {
      const formData = new FormData();
      formData?.append(UPLOAD_FILES_SERVICE?.PDF_FILE, file);
      formData?.append(UPLOAD_FILES_SERVICE?.DOMAIN, domain);
      formData?.append(UPLOAD_FILES_SERVICE?.SUBDOMAIN, subDomain);
      formData?.append(UPLOAD_FILES_SERVICE?.COURSE_NAME, courseName);

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS?.FILE_UPLOAD}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
        },
        body: formData,
      });

      if (!response?.ok) {
        throw new Error(
          `${UPLOAD_FILES_SERVICE?.ERROR_UPLOAD_FAILED}: ${response?.status} - ${response?.statusText}`
        );
      }
      const data = await response?.json();
      return data;
    } catch (error) {
      console.error(UPLOAD_FILES_SERVICE?.ERROR_UPLOADING_FILE, error);
    }
  } else {
    alert(UPLOAD_FILES_SERVICE?.ERROR_MISSING_FIELDS);
  }
};

export const sendEmail = async (mailData: {
  email: string;
  subject: string;
  response: string;
  query: string;
}) => {
  if (mailData) {
    try {
      const payload = {
        email: mailData?.email,
        subject: mailData?.subject,
        response: mailData?.response,
        query: mailData?.query,
      };

      const responseObj = await fetch(
        `${API_BASE_URL}${ENDPOINTS?.SEND_MAIL}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(
              CONSTANTS?.REACT_TOKEN
            )}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!responseObj?.ok) {
        throw new Error(
          `Failed to send email: ${responseObj?.status} - ${responseObj?.statusText}`
        );
      }

      const data = await responseObj?.json();
      return data;
    } catch (error) {
      console.error("Error sending email:", error);
    }
  } else {
    alert("All fields are required to send the email.");
  }
};

export const fetchUserQueries = async (user_id: string): Promise<any> => {
  const userId = user_id ? user_id : sessionStorage.getItem(CONSTANTS?.USER_ID);

  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS?.USER_QUERIES(userId ?? "")}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
          "Content-Type": "application/json",
        },
        redirect: "follow",
      }
    );

    if (!response?.ok) {
      throw new Error("Failed to fetch user details");
    }

    const data = response?.json();
    return data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const deleteEditedEmbeddings = async (userId: string): Promise<any> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS?.DELETE_EDITED_EMBEDDINGS}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content_hash: userId,
        }),
        redirect: "follow",
      }
    );

    if (!response?.ok) {
      throw new Error("Failed to delete edited embeddings");
    }

    const data = response?.json();
    return data;
  } catch (error) {
    console.error("Error deleting edited embeddings:", error);
    throw error;
  }
};

export interface EditResponsePayload {
  question: string;
  edit_response: string;
  admin_username: string;
  course_name: string;
}

export const editResponse = async (
  payload: EditResponsePayload
): Promise<any> => {
  try {
    const response = await fetch(
      // `https://api-dev.techsophy.com/api/document-processor/v1/edit/response`,
      `${API_BASE_URL}${ENDPOINTS?.EDIT_RESPONSE}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(
            CONSTANTS?.REACT_TOKEN
          )}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response?.ok) {
      throw new Error("Failed to edit response");
    }

    const data = await response?.json();
    return data;
  } catch (error) {
    console.error("Error editing response:", error);
    throw error;
  }
};
